<script setup lang="ts">
import { VTextField, VTextArea, VButton, VLoadingSection } from '@/modules/shared/components'
import { computed, ref, watch } from 'vue'
import VInputRadio from './v-input-radio.vue'
import { sameAs } from '@vuelidate/validators'
import useVuelidate from '@vuelidate/core'
import { scrollToError } from '@/modules/shared/utils/form'

const props = defineProps<{
  disbursementPreferences: any
  inDisplayMode?: boolean
  isWizard?: boolean
  onSubmit: (payload: any) => Promise<void>
}>()

const isWizard = computed(() => !!props.isWizard)
const isFieldDisabled = computed(() => !!props.inDisplayMode)
const currentDisbursementPreferences = computed(() => props.disbursementPreferences)
const skeleton = computed(() => !currentDisbursementPreferences.value)

///////////////////////////////////////////////////////////////////////////////
// Form
///////////////////////////////////////////////////////////////////////////////

const original_bank_account_number = ref(null)
const check_bank_account_number = computed(
  () => original_bank_account_number.value !== disbursementPreferenceForm.value.disbursement_wire_bank_account_number,
)

const disbursementPreferenceInitialState = {
  disbursement_method: null,
  disbursement_wire_bank_name: null,
  disbursement_wire_bank_address: null,
  disbursement_wire_bank_routing_number: null,
  disbursement_wire_bank_account_number: null,
  disbursement_wire_repeat_bank_account_number: null,
  disbursement_wire_bank_swift_code: null,
  disbursement_wire_account_name: null,
  disbursement_wire_for_further_credit_to: null,
  disbursement_wire_bank_notes: null,
  disbursement_check_receiver_name: null,
  disbursement_other_details: null,
}

const disbursementPreferenceRules = {
  disbursement_wire_repeat_bank_account_number: {
    sameAsRepeat: sameAs(
      computed(() =>
        check_bank_account_number.value
          ? disbursementPreferenceForm.value.disbursement_wire_bank_account_number
          : disbursementPreferenceForm.value.disbursement_wire_repeat_bank_account_number,
      ),
    ),
  },
}
const disbursementPreferenceForm = ref({ ...disbursementPreferenceInitialState })
const v$ = useVuelidate(disbursementPreferenceRules, disbursementPreferenceForm, { $lazy: true })

const setForm = () => {
  const keys = Object.keys(disbursementPreferenceInitialState)
  keys.forEach((key) => {
    disbursementPreferenceForm.value[key] = currentDisbursementPreferences.value[key] || null
  })

  original_bank_account_number.value = currentDisbursementPreferences.value.disbursement_wire_bank_account_number
}

const resetForm = () => {
  v$.value.$reset()
  setForm()
}

watch(currentDisbursementPreferences, (value) => {
  if (value) setForm()
})

const isValid = async () => {
  const valid = await v$.value.$validate()
  return valid
}

const submit = async () => {
  const valid = await v$.value.$validate()
  if (!valid) {
    scrollToError(v$, Object.keys(disbursementPreferenceRules))
    return
  }

  const payload = { ...disbursementPreferenceForm.value }
  await props.onSubmit(payload)
}

const disbursementMethodOptions = [
  { label: 'Wire transfer', value: 'wire' },
  { label: 'Check', value: 'check' },
  { label: 'Other', value: 'other' },
]

const onClickDisbursementMethodOption = (option) => {
  if (isFieldDisabled.value) return
  disbursementPreferenceForm.value.disbursement_method = option.value
}

const errors = computed(() => {
  return {
    disbursement_method: v$.value['disbursement_method']?.$errors[0]?.$message,
  }
})

defineExpose({
  isValid,
  resetForm,
  submit,
})
</script>

<template>
  <div class="w-full">
    <div class="rounded-lg border-[1px] p-5">
      <div class="mb-5 flex items-center justify-between">
        <div class="text-lg font-semibold">How do you want to receive disbursements?</div>
        <div class="flex items-center gap-3">
          <slot name="actions"></slot>
        </div>
      </div>
      <div class="space-y-6" v-if="!skeleton">
        <div class="flex flex-col items-baseline gap-1">
          <div class="flex gap-3">
            <div
              class="flex items-center gap-2 rounded-lg bg-gray-100 px-4 py-2"
              :class="{
                'cursor-pointer': !isFieldDisabled,
                'border-[1px] border-[#3d8a9d]': disbursementPreferenceForm.disbursement_method === option.value,
              }"
              @click="() => onClickDisbursementMethodOption(option)"
              v-for="option in disbursementMethodOptions"
              :key="option.value"
            >
              <input
                v-model="disbursementPreferenceForm.disbursement_method"
                type="radio"
                :value="option.value"
                :disabled="isFieldDisabled"
                class="focus:ring-0"
              />
              <div class="">
                {{ option.label }}
              </div>
            </div>
          </div>
          <div v-if="errors.disbursement_method" class="mt-1 text-sm text-red-500">
            {{ errors.disbursement_method }}
          </div>
        </div>
        <template v-if="disbursementPreferenceForm.disbursement_method === 'wire'">
          <VTextField
            v-model="disbursementPreferenceForm.disbursement_wire_bank_name"
            label="Bank Name"
            property="disbursement_wire_bank_name"
            :v$="v$"
            :disabled="isFieldDisabled"
            disabledVariant="light"
          />
          <VTextArea
            label="Bank Address"
            v-model="disbursementPreferenceForm.disbursement_wire_bank_address"
            :v$="v$"
            property="disbursement_wire_bank_address"
            :disabled="isFieldDisabled"
            disabledVariant="light"
          />
          <div class="mb-5 flex gap-5">
            <VTextField
              v-model="disbursementPreferenceForm.disbursement_wire_bank_routing_number"
              label="Bank Routing Number"
              property="disbursement_wire_bank_routing_number"
              type="text"
              :v$="v$"
              class="w-1/2"
              :disabled="isFieldDisabled"
              disabledVariant="light"
            />
            <VTextField
              v-model="disbursementPreferenceForm.disbursement_wire_bank_swift_code"
              label="Bank Swift Code"
              property="disbursement_wire_bank_swift_code"
              type="text"
              :v$="v$"
              class="w-1/2"
              :disabled="isFieldDisabled"
              disabledVariant="light"
            />
          </div>
          <VTextField
            v-model="disbursementPreferenceForm.disbursement_wire_bank_account_number"
            label="Bank Account Number"
            property="disbursement_wire_bank_account_number"
            type="text"
            :v$="v$"
            :disabled="isFieldDisabled"
            disabledVariant="light"
          />
          <VTextField
            v-model="disbursementPreferenceForm.disbursement_wire_repeat_bank_account_number"
            label="Verify Bank Account Number"
            property="disbursement_wire_repeat_bank_account_number"
            type="text"
            :v$="v$"
            :disabled="isFieldDisabled"
            v-if="check_bank_account_number"
            disabledVariant="light"
          />
          <VTextField
            v-model="disbursementPreferenceForm.disbursement_wire_account_name"
            label="Bank Account Name"
            property="disbursement_wire_account_name"
            type="text"
            :v$="v$"
            :disabled="isFieldDisabled"
            disabledVariant="light"
          />
          <VTextField
            v-model="disbursementPreferenceForm.disbursement_wire_for_further_credit_to"
            label="For Further Credit To"
            property="disbursement_wire_for_further_credit_to"
            type="text"
            :v$="v$"
            :disabled="isFieldDisabled"
            disabledVariant="light"
          />
          <VTextArea
            v-model="disbursementPreferenceForm.disbursement_wire_bank_notes"
            label="Additional Banking Details"
            property="disbursement_wire_bank_notes"
            :disabled="isFieldDisabled"
            placeholder="Enter any additional wire instructions, intermediary bank details, SWIFT/BIC codes, or special instructions"
          />
        </template>
        <template v-if="disbursementPreferenceForm.disbursement_method === 'check'">
          <VTextField
            v-model="disbursementPreferenceForm.disbursement_check_receiver_name"
            label="Make Check Payable To"
            property="disbursement_check_receiver_name"
            type="text"
            :v$="v$"
            :disabled="isFieldDisabled"
            disabledVariant="light"
          />
        </template>
        <template v-if="disbursementPreferenceForm.disbursement_method === 'other'">
          <VTextArea
            label="Other Details"
            v-model="disbursementPreferenceForm.disbursement_other_details"
            :v$="v$"
            property="disbursement_other_details"
            :disabled="isFieldDisabled"
            disabledVariant="light"
          />
        </template>
      </div>
      <VLoadingSection
        v-else
        class="my-40"
        title="Preparing Your Information"
        description="Please wait a moment while we set things up for you. Your data is being processed to ensure everything runs
      smoothly."
      />
    </div>
  </div>
  <div class="mt-24" v-if="!isFieldDisabled && !isWizard">
    <VButton variant="v-blue" size="lg" :click="submit">Confirm Changes</VButton>
  </div>
</template>

<style scoped>
.terms-checkbox:checked,
.terms-checkbox:checked:focus,
.terms-checkbox:checked:hover,
[type='radio']:checked,
[type='radio']:checked:focus,
[type='radio']:checked:hover {
  background-color: #3d8a9d;
}
</style>
